/**
 * AnchoredPopover — универсальный поповер, привязанный к DOM-элементу.
 *
 * Используется для desktop-режима sheets (AlbumEditSheet, SunoActionsSheet,
 * TrackFolderMenuSheet, AddToAlbumSheet) — на большом экране шторка-снизу
 * выглядит тяжело, лучше мини-окно около кнопки, которая его открыла.
 *
 * Фичи:
 *   - Авто-позиционирование: предпочтительно снизу anchor'а с правой стороной
 *     выровненной по правой стороне anchor'а. Если не влезает — открывается
 *     сверху. НЕ clamp'ится к viewport — popover жёстко приклеен к anchor'у,
 *     уезжает с ним при скролле и исчезает за краем экрана.
 *   - Стрелка-указатель на anchor (10×10 rotated square), X-позиция
 *     вычисляется из центра anchor'а.
 *   - Закрытие: клик снаружи + Escape.
 *   - Morph-анимация: popover «вырастает» из точки где стрелка упирается
 *     в кнопку — transform-origin на этой точке + scale 0.5→1 + fade. При
 *     закрытии — обратная анимация (scale 1→0.5, fade), после её завершения
 *     DOM размонтируется. Создаёт ощущение что popover рождается и
 *     возвращается в саму кнопку.
 *   - Рендер через Portal — не зависит от overflow/z-index родителей.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const EXIT_DURATION_MS = 160 // Должно совпадать с анимацией в SCSS

interface Props {
	open: boolean
	onClose: () => void
	/** DOM-элемент к которому привязан popover (обычно кнопка, открывшая его). */
	anchorEl: HTMLElement | null
	/** Ширина popover'а в px. По умолчанию 320. Actions sheet использует 380+. */
	width?: number
	/** Горизонтальное выравнивание popover'а относительно anchor'а:
	 *   - 'right'  (default) — правый край popover'а совпадает с правым краем anchor'а.
	 *                          Хорошо для узких popover'ов в правой части экрана.
	 *   - 'center'           — центр popover'а по центру anchor'а. Естественнее
	 *                          для широких popover'ов (SunoActionsSheet, AlbumEditSheet).
	 *   - 'left'             — левый край popover'а совпадает с левым краем anchor'а
	 *                          (меню форматов под иконкой у левого края карточки, 23.10). */
	align?: 'right' | 'center' | 'left'
	/** Zindex — если нужно выше стандартного (для вложенных поповеров). */
	zIndex?: number
	/** Явное направление открытия: 'above' — над anchor'ом, 'below' — под ним.
	 *  Без пропа направление выбирается эвристикой по свободному месту —
	 *  она может ошибиться, если контент грузится позже (скелетон ниже
	 *  реального контента). Anchor у нижнего края экрана (поле ввода чата) →
	 *  задавай 'above'. ResizeObserver-флип при нехватке места сохраняется. */
	preferDirection?: 'above' | 'below'
	/** Дополнительный className для корневого div. */
	className?: string
	children: React.ReactNode
}

const DEFAULT_WIDTH = 320
// GAP = 0 — popover вплотную к anchor-кнопке. Кнопка в своём --active
// состоянии превращается в «язычок папки» (rounded top, flat bottom, тот же
// фон что у popover'а) и визуально сливается с popover-card'ом в одну
// силуэтную форму. Отдельной стрелки-указателя больше нет — сам tab-кнопка
// её замещает.
const GAP = 0
const VIEWPORT_MARGIN = 8
const ARROW_MARGIN = 18 // минимум от края popover'а до точки transform-origin (чтобы при scale не выезжало за rounded-corner)

export default function AnchoredPopover({
	open,
	onClose,
	anchorEl,
	width = DEFAULT_WIDTH,
	align = 'right',
	zIndex = 500,
	className,
	children,
	preferDirection,
}: Props) {
	const popRef = useRef<HTMLDivElement>(null)
	// Направление (up/down) залочивается на первом успешном расчёте и не
	// меняется при скролле — иначе popover «прыгает» между сверху/снизу
	// anchor'а пока юзер скроллит. Сбрасывается при закрытии/ре-открытии.
	const directionRef = useRef<'up' | 'down' | null>(null)

	// Mount vs open:
	//   open=true  → mount сразу, enter-анимация
	//   open=false → начинаем exit-анимацию, DOM остаётся mounted пока она
	//                не закончится, только потом unmount (setMounted(false))
	// Это даёт закрывающую анимацию (scale 1→0.5 + fade) — popover визуально
	// «втягивается» обратно в кнопку, а не пропадает мгновенно.
	const [mounted, setMounted] = useState(open)
	const [isExiting, setIsExiting] = useState(false)

	// Синхронизация open→mounted БЕЗ setState в теле эффекта
	// (react-hooks/set-state-in-effect): корректировка состояния прямо в
	// рендере — паттерн «adjusting state during render» из доков React.
	// Unmount после exit-анимации — таймером в эффекте ниже (в callback
	// setState разрешён).
	const [prevOpen, setPrevOpen] = useState(open)
	if (open !== prevOpen) {
		setPrevOpen(open)
		if (open) {
			setMounted(true)
			setIsExiting(false)
		} else if (mounted) {
			// open перешёл в false — стартуем exit-анимацию
			setIsExiting(true)
		}
	}

	useEffect(() => {
		if (!isExiting) return
		const timer = setTimeout(() => {
			setMounted(false)
			setIsExiting(false)
		}, EXIT_DURATION_MS)
		return () => clearTimeout(timer)
	}, [isExiting])

	const [pos, setPos] = useState<{
		top: number
		left: number
		arrowLeft: number
		arrowDirection: 'up' | 'down'
		/** Максимальная высота popover-контента — считается динамически из
		 *  свободного места между popover.top и нижним (или верхним) краем
		 *  viewport'а. Контент внутри скроллится если превышает maxHeight. */
		maxHeight: number
		ready: boolean
	}>({
		top: 0,
		left: 0,
		arrowLeft: width / 2,
		arrowDirection: 'up',
		maxHeight: 0,
		ready: false,
	})

	useLayoutEffect(() => {
		if (!mounted || !anchorEl) {
			directionRef.current = null
			return
		}
		// Сбрасываем направление на каждое новое открытие (anchor/open change)
		directionRef.current = null

		// Пересчитываем позицию. Вызывается при открытии (нужна реальная
		// высота popover'а) и на каждый scroll/resize чтобы popover ехал
		// вместе с anchor'ом (position:fixed сам по себе не следует за
		// скроллом страницы, без этого popover «отклеивается» от кнопки).
		//
		// Ключевой момент: НЕ clamp'им top к viewport. Popover жёстко привязан
		// к anchor'у — если юзер проскроллил кнопку за пределы экрана, popover
		// уезжает вместе с ней (top становится отрицательным → position:fixed
		// уводит popover за верхний край viewport'а, он естественно
		// «исчезает»). Когда юзер скроллит обратно — popover появляется снова.
		const updatePosition = (canFlipDirection: boolean = false) => {
			if (!anchorEl) return
			const rect = anchorEl.getBoundingClientRect()

			// X-координата: зависит от align
			//   'right'  — правый край popover'а совпадает с правым краем anchor'а
			//   'center' — центр popover'а по центру anchor'а
			//   'left'   — левый край popover'а совпадает с левым краем anchor'а
			// Горизонтальный clamp оставляем — на этой странице юзер не
			// скроллит по X, только вертикально.
			let left =
				align === 'center'
					? rect.left + rect.width / 2 - width / 2
					: align === 'left'
						? rect.left
						: rect.right - width
			left = Math.max(
				VIEWPORT_MARGIN,
				Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN),
			)

			// `|| 400` (не `?? 400`): если popover в DOM, но контент ещё не
			// layout'нулся (offsetHeight === 0), 0-fallback дал бы неправильное
			// направление. 400 — разумная оценка пока контент не пришёл.
			const popH = popRef.current?.offsetHeight || 400

			// Direction:
			//   - На первом расчёте — вычисляем по spaceBelow vs popH/spaceAbove.
			//   - На scroll — НЕ меняем (иначе popover «прыгает» при скролле).
			//   - На ResizeObserver (popover вырос — например подгрузились
			//     данные) — пересчитываем direction если в текущем больше не
			//     помещается. Это лечит частый кейс «открыл popover, скелетон
			//     был мелкий, направление выбрано вниз; данные подгрузились,
			//     popover вырос, упёрся в нижний край viewport'а».
			const spaceBelow = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN
			const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN

			if (directionRef.current === null) {
				// Первая инициализация. Явный preferDirection перекрывает
				// эвристику (внутри: 'up' = popover ПОД anchor'ом, 'down' = НАД).
				if (preferDirection) {
					directionRef.current = preferDirection === 'above' ? 'down' : 'up'
				} else if (spaceBelow >= popH || spaceBelow >= spaceAbove) {
					directionRef.current = 'up'
				} else {
					directionRef.current = 'down'
				}
			} else if (canFlipDirection) {
				// Re-evaluation после роста контента — переключаемся только если
				// текущий direction явно не помещается, а противоположный
				// помещается значительно лучше. Hysteresis: разница >= 80px,
				// чтобы не флипало туда-сюда от мелких изменений.
				const HYSTERESIS = 80
				if (directionRef.current === 'up' && spaceBelow < popH && spaceAbove >= spaceBelow + HYSTERESIS) {
					directionRef.current = 'down'
				} else if (directionRef.current === 'down' && spaceAbove < popH && spaceBelow >= spaceAbove + HYSTERESIS) {
					directionRef.current = 'up'
				}
			}

			const top =
				directionRef.current === 'up'
					? rect.bottom + GAP
					: rect.top - popH - GAP

			// Динамический max-height — чтобы popover не выходил за край
			// viewport'а. Свободное пространство от popover.top до края
			// окна (снизу для 'up', сверху для 'down'). Если контент больше —
			// скроллится внутри через overflow-y:auto на __content.
			const maxHeight =
				directionRef.current === 'up'
					? window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN
					: rect.top - GAP - VIEWPORT_MARGIN

			// «Точка tab'а» — X-координата центра anchor'а внутри popover'а.
			// Используется для transform-origin scale-анимации.
			const anchorCenterX = rect.left + rect.width / 2
			const rawArrowLeft = anchorCenterX - left
			const arrowLeft = Math.max(ARROW_MARGIN, Math.min(rawArrowLeft, width - ARROW_MARGIN))

			setPos({
				top,
				left,
				arrowLeft,
				arrowDirection: directionRef.current,
				maxHeight: Math.max(120, maxHeight), // минимум 120px, иначе меню бесполезно
				ready: true,
			})
		}

		updatePosition()

		// Scroll-трекинг. `capture: true` чтобы ловить скролл любого
		// ancestor'а anchor'а (popover может быть запущен из скроллируемого
		// модального/контейнера). requestAnimationFrame debounce'ит пересчёт
		// до одного раза за кадр — при 60fps плавный без фризов.
		// Direction на scroll НЕ меняется (canFlipDirection=false по умолчанию).
		let rafId = 0
		const schedule = () => {
			if (rafId) return
			rafId = requestAnimationFrame(() => {
				rafId = 0
				updatePosition(false)
			})
		}

		window.addEventListener('scroll', schedule, true)
		window.addEventListener('resize', schedule)

		// Следим за размером самого popover'а — при смене контента (accordion
		// в TrackFolderMenuSheet step 'menu'↔'addToAlbum', toggle треков в
		// AlbumEditSheet, async загрузка тем у GammaThemePicker, etc.) высота
		// меняется. Здесь `canFlipDirection=true`: если popover вырос и
		// больше не помещается в текущем направлении — переключаемся.
		let resizeRafId = 0
		const scheduleFlip = () => {
			if (resizeRafId) return
			resizeRafId = requestAnimationFrame(() => {
				resizeRafId = 0
				updatePosition(true)
			})
		}
		let resizeObserver: ResizeObserver | null = null
		if (popRef.current && typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(scheduleFlip)
			resizeObserver.observe(popRef.current)
		}

		return () => {
			window.removeEventListener('scroll', schedule, true)
			window.removeEventListener('resize', schedule)
			resizeObserver?.disconnect()
			if (rafId) cancelAnimationFrame(rafId)
			if (resizeRafId) cancelAnimationFrame(resizeRafId)
		}
	}, [mounted, anchorEl, width, align, preferDirection])

	// Click outside → close. Игнорируем:
	//   1. Клик по самому popover'у — очевидно
	//   2. Клик по anchor'у — тот же клик (который открыл popover) не должен
	//      сразу закрыть его
	//   3. Клик внутри ЛЮБОГО другого Sheet/popover открытого поверх нас.
	//      React-stopPropagation не блокирует native mousedown → без этой
	//      проверки клик по textarea во вложенном Sheet'е (SunoReplaceSection
	//      и т.п., рендерится через portal на document.body) закрывал бы
	//      родительский popover. Heuristic: closest('.sheet-backdrop') и
	//      closest('.anchored-popover') покрывает все наши overlay-компоненты.
	//   4. Клик внутри SpotlightTour (.spt__tooltip, .spt__panel) — без этого
	//      нажатие «Дальше»/«Назад»/«Пропустить»/клик по dimmed-панели
	//      закрывали бы underlying popover (тур рендерится через portal к
	//      document.body, не является потомком popover'а).
	useEffect(() => {
		if (!open || !anchorEl) return
		const handler = (e: MouseEvent) => {
			const target = e.target as Node
			if (popRef.current?.contains(target)) return
			if (anchorEl.contains(target)) return
			// Клик в другом overlay — не наш outside
			const el = target as Element
			if (el.closest?.('.sheet-backdrop')) return
			if (el.closest?.('.anchored-popover')) return
			if (el.closest?.('.spt__tooltip')) return
			if (el.closest?.('.spt__panel')) return
			onClose()
		}
		const t = setTimeout(() => document.addEventListener('mousedown', handler), 0)
		return () => {
			clearTimeout(t)
			document.removeEventListener('mousedown', handler)
		}
	}, [open, anchorEl, onClose])

	// Escape → close
	useEffect(() => {
		if (!open) return
		const handler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('keydown', handler)
		return () => document.removeEventListener('keydown', handler)
	}, [open, onClose])

	if (!mounted || !anchorEl) return null

	// Transform-origin на точке стрелки: popover «рождается» именно из того
	// места где arrow упирается в кнопку. Для direction='up' (popover снизу,
	// стрелка вверх) origin = верх popover'а. Для 'down' — низ.
	const transformOrigin = `${pos.arrowLeft}px ${pos.arrowDirection === 'up' ? '0' : '100%'}`

	// Адаптивные углы popover'а: тот угол которым popover прилегает к
	// tab-кнопке должен быть острым (0), иначе rounded-curve создаёт
	// видимый «notch» ниже кнопки. Зависит от align + direction:
	//   align='right' + direction='up' — tab в top-right → top-right=0
	//   align='right' + direction='down' — tab в bottom-right → bottom-right=0
	// Для 'center' кнопка в середине, корнеры popover'а вдалеке — не трогаем.
	const sharpCorners: React.CSSProperties = {}
	if (align === 'right') {
		if (pos.arrowDirection === 'up') {
			sharpCorners.borderTopRightRadius = 0
		} else {
			sharpCorners.borderBottomRightRadius = 0
		}
	}

	return createPortal(
		<div
			ref={popRef}
			className={
				'anchored-popover' +
				` anchored-popover--${pos.arrowDirection}` +
				(isExiting ? ' anchored-popover--exiting' : '') +
				(className ? ' ' + className : '')
			}
			style={{
				position: 'fixed',
				top: pos.top,
				left: pos.left,
				width,
				zIndex,
				visibility: pos.ready ? 'visible' : 'hidden',
				transformOrigin,
				...sharpCorners,
			}}
			role="dialog"
		>
			{/* Стрелки нет — anchor-кнопка в --active превращается в «язычок»
			    (tab) который сливается с popover'ом в одну форму. Transform-
			    origin на центре tab обеспечивает scale-анимацию «вырастания»
			    из кнопки. */}
			<div
				className="anchored-popover__content"
				style={{ maxHeight: pos.maxHeight }}
			>
				{children}
			</div>
		</div>,
		document.body,
	)
}

/** Хук: проверка матчит ли viewport desktop-breakpoint (по умолчанию 768px). */
// eslint-disable-next-line react-refresh/only-export-components -- хук исторически живёт рядом с компонентом; вынос сломал бы импорты по проекту
export function useIsDesktopPopover(minWidth = 768): boolean {
	const [isDesktop, setIsDesktop] = useState(
		() => typeof window !== 'undefined' && window.innerWidth >= minWidth,
	)
	useEffect(() => {
		const media = window.matchMedia(`(min-width: ${minWidth}px)`)
		const handler = () => setIsDesktop(media.matches)
		media.addEventListener('change', handler)
		return () => media.removeEventListener('change', handler)
	}, [minWidth])
	return isDesktop
}
